/**
 * Charmify — Product Gallery
 *
 * Features:
 *  - Click thumbnail to switch main image
 *  - Hover zoom on desktop (transform scale with mouse position tracking)
 *  - Mobile swipe gestures for image carousel
 *  - Active thumbnail state management
 *  - Lazy loading of off-screen images
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Constants                                                           */
  /* ------------------------------------------------------------------ */

  var ZOOM_SCALE = 2.0;
  var SWIPE_THRESHOLD = 50; // px minimum to register a swipe
  var ACTIVE_CLASS = 'is-active';

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  var currentIndex = 0;
  var slideCount = 0;

  /* ------------------------------------------------------------------ */
  /* DOM references (populated in init)                                  */
  /* ------------------------------------------------------------------ */

  var section = null;
  var mediaZoom = null;
  var slides = [];
  var thumbnails = [];
  var dots = [];

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function isMobile() {
    return window.innerWidth < 1024;
  }

  /**
   * Preload an image src that has loading="lazy" when it is about to be shown.
   */
  function preloadSlideImage(index) {
    if (index < 0 || index >= slideCount) return;
    var img = slides[index].querySelector('img[loading="lazy"]');
    if (img) {
      img.removeAttribute('loading');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Core: switch to a given slide index                                 */
  /* ------------------------------------------------------------------ */

  function goToSlide(index) {
    if (index < 0 || index >= slideCount) return;
    if (index === currentIndex) return;

    // Preload the target image before transition
    preloadSlideImage(index);

    // Desktop: fade transition (handled by CSS .is-active)
    if (!isMobile()) {
      slides[currentIndex].classList.remove(ACTIVE_CLASS);
      slides[currentIndex].setAttribute('aria-hidden', 'true');

      slides[index].classList.add(ACTIVE_CLASS);
      slides[index].removeAttribute('aria-hidden');
    } else {
      // Mobile: scroll to the slide
      var slideEl = slides[index];
      if (mediaZoom && slideEl) {
        mediaZoom.scrollTo({
          left: slideEl.offsetLeft,
          behavior: 'smooth'
        });
      }
    }

    // Update thumbnails
    if (thumbnails.length) {
      thumbnails.forEach(function (thumb) {
        thumb.classList.remove(ACTIVE_CLASS);
        thumb.setAttribute('aria-current', 'false');
      });
      if (thumbnails[index]) {
        thumbnails[index].classList.add(ACTIVE_CLASS);
        thumbnails[index].setAttribute('aria-current', 'true');
      }
    }

    // Update dots
    if (dots.length) {
      dots.forEach(function (dot) {
        dot.classList.remove(ACTIVE_CLASS);
      });
      if (dots[index]) {
        dots[index].classList.add(ACTIVE_CLASS);
      }
    }

    currentIndex = index;
  }

  /* ------------------------------------------------------------------ */
  /* Thumbnails: click                                                   */
  /* ------------------------------------------------------------------ */

  function handleThumbnailClick(event) {
    var btn = event.currentTarget;
    var index = parseInt(btn.getAttribute('data-thumbnail-index'), 10);
    if (!isNaN(index)) {
      goToSlide(index);
    }
  }

  function bindThumbnails() {
    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', handleThumbnailClick);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Hover zoom (desktop only)                                           */
  /* ------------------------------------------------------------------ */

  function handleZoomMouseMove(event) {
    if (isMobile()) return;

    var rect = mediaZoom.getBoundingClientRect();
    var x = ((event.clientX - rect.left) / rect.width) * 100;
    var y = ((event.clientY - rect.top) / rect.height) * 100;

    var activeSlide = slides[currentIndex];
    if (!activeSlide) return;

    var img = activeSlide.querySelector('.product-main__media-image');
    if (!img) return;

    img.style.transformOrigin = x + '% ' + y + '%';
    img.style.transform = 'scale(' + ZOOM_SCALE + ')';
  }

  function handleZoomMouseLeave() {
    if (isMobile()) return;

    var activeSlide = slides[currentIndex];
    if (!activeSlide) return;

    var img = activeSlide.querySelector('.product-main__media-image');
    if (!img) return;

    img.style.transformOrigin = 'center center';
    img.style.transform = 'scale(1)';
  }

  function bindZoom() {
    if (!mediaZoom) return;
    mediaZoom.addEventListener('mousemove', handleZoomMouseMove);
    mediaZoom.addEventListener('mouseleave', handleZoomMouseLeave);
  }

  /* ------------------------------------------------------------------ */
  /* Mobile swipe                                                        */
  /* ------------------------------------------------------------------ */

  var touchStartX = 0;
  var touchStartY = 0;
  var touchDeltaX = 0;
  var isSwiping = false;

  function handleTouchStart(event) {
    if (!isMobile()) return;
    var touch = event.touches[0];
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    touchDeltaX = 0;
    isSwiping = false;
  }

  function handleTouchMove(event) {
    if (!isMobile()) return;
    var touch = event.touches[0];
    touchDeltaX = touch.clientX - touchStartX;
    var deltaY = Math.abs(touch.clientY - touchStartY);

    // Only register horizontal swipe if it's more horizontal than vertical
    if (Math.abs(touchDeltaX) > deltaY && Math.abs(touchDeltaX) > 10) {
      isSwiping = true;
    }
  }

  function handleTouchEnd() {
    if (!isMobile() || !isSwiping) return;

    if (touchDeltaX < -SWIPE_THRESHOLD && currentIndex < slideCount - 1) {
      goToSlide(currentIndex + 1);
    } else if (touchDeltaX > SWIPE_THRESHOLD && currentIndex > 0) {
      goToSlide(currentIndex - 1);
    }

    isSwiping = false;
  }

  /**
   * On mobile, listen for scroll events on the media container to update
   * the active dot indicator as the user scrolls naturally.
   */
  var scrollDebounce = null;

  function handleMobileScroll() {
    if (!isMobile() || !mediaZoom) return;

    clearTimeout(scrollDebounce);
    scrollDebounce = setTimeout(function () {
      var scrollLeft = mediaZoom.scrollLeft;
      var slideWidth = mediaZoom.offsetWidth;

      if (slideWidth === 0) return;

      var newIndex = Math.round(scrollLeft / slideWidth);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < slideCount) {
        // Update dots and current index without triggering another scroll
        if (dots.length) {
          dots.forEach(function (dot) { dot.classList.remove(ACTIVE_CLASS); });
          if (dots[newIndex]) dots[newIndex].classList.add(ACTIVE_CLASS);
        }
        currentIndex = newIndex;
      }
    }, 80);
  }

  function bindSwipe() {
    if (!mediaZoom) return;
    mediaZoom.addEventListener('touchstart', handleTouchStart, { passive: true });
    mediaZoom.addEventListener('touchmove', handleTouchMove, { passive: true });
    mediaZoom.addEventListener('touchend', handleTouchEnd, { passive: true });
    mediaZoom.addEventListener('scroll', handleMobileScroll, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Lazy load: preload adjacent images                                  */
  /* ------------------------------------------------------------------ */

  function preloadAdjacentSlides() {
    preloadSlideImage(currentIndex - 1);
    preloadSlideImage(currentIndex + 1);
  }

  /* ------------------------------------------------------------------ */
  /* Keyboard navigation                                                 */
  /* ------------------------------------------------------------------ */

  function handleKeyboard(event) {
    // Only respond when gallery or a thumbnail is focused
    if (!section.contains(document.activeElement)) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      goToSlide(currentIndex - 1);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      goToSlide(currentIndex + 1);
    }
  }

  function bindKeyboard() {
    document.addEventListener('keydown', handleKeyboard);
  }

  /* ------------------------------------------------------------------ */
  /* Variant image switching                                             */
  /* ------------------------------------------------------------------ */

  /**
   * When the configurator changes the variant, it dispatches a
   * 'charmify:variant:change' event with detail.featured_media_id.
   * The gallery listens and switches to that image.
   */
  function handleVariantMediaChange(event) {
    var mediaId = event.detail && event.detail.featured_media_id;
    if (!mediaId) return;

    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
        goToSlide(i);
        break;
      }
    }
  }

  function bindVariantChange() {
    document.addEventListener('charmify:variant:change', handleVariantMediaChange);
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    section = document.querySelector('.product-main');
    if (!section) return;

    var sectionId = section.getAttribute('data-section-id');

    mediaZoom = document.getElementById('ProductMainMedia-' + sectionId);
    slides = Array.prototype.slice.call(
      section.querySelectorAll('.product-main__media-slide')
    );
    thumbnails = Array.prototype.slice.call(
      section.querySelectorAll('.product-main__thumbnail')
    );
    dots = Array.prototype.slice.call(
      section.querySelectorAll('.product-main__swipe-dot')
    );

    slideCount = slides.length;
    if (slideCount === 0) return;

    // Determine initial active index
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains(ACTIVE_CLASS)) {
        currentIndex = i;
        break;
      }
    }

    bindThumbnails();
    bindZoom();
    bindSwipe();
    bindKeyboard();
    bindVariantChange();

    // Preload neighbours of the first slide
    preloadAdjacentSlides();
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify section rendering API: re-init when the section is re-rendered
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('.product-main')) {
      init();
    }
  });
})();
